import api from "./api";
import { applyCountryCode } from "../utils/Formatters";

async function createPaymentRequest({
  amountRequested,
  invoicesRequested,
  allowAdditionalInvoices,
  invoicesRequestedAreMandatory,
  includePdfStatement,
  phone,
  email,
  communicationMethod,
  reference,
  customerName,
  customerId,
  subject,
  message,
  emailBody,
  payerBaseUrl,
  files,
  allowCardPayment,
  allowKlarnaPayment,
  allowOpenBankingPayment = null,
  allowAmexPayment = null,
  messageTemplateId = null,
  messageTemplateName = null,
  batchId = null,
  sendAsPlainText = false,
  bypassInterceptor = false,
  salesOrderIds = null,
  emailContentFormat = null,
  paymentProfileId = null,
  paymentRequestTemplateId = null,
}) {
  var data = new FormData();

  if (amountRequested || amountRequested === 0) {
    data.append("amountRequested", amountRequested);
  }
  if (invoicesRequested) {
    data.append("invoicesRequested", invoicesRequested);
  }
  if (allowAdditionalInvoices === false || allowAdditionalInvoices === true) {
    data.append("allowAdditionalInvoices", allowAdditionalInvoices);
  }
  if (
    invoicesRequestedAreMandatory === false ||
    invoicesRequestedAreMandatory === true
  ) {
    data.append("invoicesRequestedAreMandatory", invoicesRequestedAreMandatory);
  }
  if (includePdfStatement === false || includePdfStatement === true) {
    data.append("includePdfStatement", includePdfStatement);
  }
  if (batchId) {
    data.append("batchId", batchId);
  }
  if (customerId) {
    data.append("customerId", customerId);
  }
  if (messageTemplateId) {
    data.append("messageTemplateId", messageTemplateId);
  }
  if (messageTemplateName) {
    data.append("messageTemplateName", messageTemplateName);
  }
  if (phone) {
    data.append("recipientPhoneNumber", applyCountryCode(phone));
  }
  if (email) {
    data.append("recipientEmail", email);
  }
  data.append("communicationMethod", communicationMethod);
  data.append("reference", reference);
  data.append("customerName", customerName);
  data.append("message", message);
  data.append("subject", subject);
  data.append("payerBaseUrl", payerBaseUrl);
  data.append("allowCardPayment", allowCardPayment);
  data.append("allowKlarnaPayment", allowKlarnaPayment);
  if (allowOpenBankingPayment !== null && allowOpenBankingPayment !== undefined) {
    data.append("allowOpenBankingPayment", allowOpenBankingPayment);
  }
  if (allowAmexPayment !== null && allowAmexPayment !== undefined) {
    data.append("allowAmexPayment", allowAmexPayment);
  }
  data.append("emailBody", emailBody);
  data.append("sendAsPlainText", sendAsPlainText);
  if (emailContentFormat) {
    data.append("emailContentFormat", emailContentFormat);
  }
  if (salesOrderIds && salesOrderIds.length > 0) {
    for (let i = 0; i < salesOrderIds.length; i++) {
      data.append("salesOrderIds", salesOrderIds[i]);
    }
  }
  if (paymentProfileId) {
    data.append("paymentProfileId", paymentProfileId);
  }
  if (paymentRequestTemplateId) {
    data.append("paymentRequestTemplateId", paymentRequestTemplateId);
  }
  if (files && files.length > 0) {
    for (let i = 0; i < files.length; i++) {
      data.append("files", files[i]);
    }
  } else {
    data.append("files", []);
  }

  const response = await api.post(
    `api/paymentrequests`,
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
    { bypassInterceptor },
  );
  return response.data;
}

async function cancelPayment(id) {
  try {
    const response = await api.post(`api/paymentrequests/${id}/cancel`, null);

    return response.data;
  } catch (error) {
    return [];
  }
}

async function markPaymentRequestPaid(id, amount, subType) {
  try {
    const response = await api.post(`/api/paymentrequests/${id}/markaspaid`, {
      amount,
      subType,
    });

    return response.data;
  } catch (error) {
    return [];
  }
}

// Throws on error so callers don't report "reminder sent" when the POST failed.
// aiSuggested tags reminders sent from an Ontik AI suggestion (the Today's-tasks page) so the
// resulting activity is attributed to the suggestion.
async function createpaymentRequestReminderAsync(
  id,
  paymentUrl,
  communicationMethod,
  recipientPhoneNumber,
  recipientEmail,
  subject,
  message,
  emailBody,
  aiSuggested = false,
) {
  const response = await api.post(`api/paymentrequests/${id}/reminders`, {
    paymentUrl,
    communicationMethod,
    recipientPhoneNumber,
    recipientEmail,
    subject,
    message,
    emailBody,
    aiSuggested,
  });

  return response.data;
}

async function getPaymentDetails(id) {
  try {
    const response = await api.get(`api/paymentrequests/${id}`);
    return response.data;
  } catch (error) {
    console.error(error);
    return [];
  }
}

// Throwing variant of getPaymentDetails for new call sites (the tasks page) that must not open a
// modal on a failed fetch. Prefer this over getPaymentDetails.
async function fetchPaymentDetails(id, { signal } = {}) {
  const response = await api.get(`api/paymentrequests/${id}`, { signal });
  return response.data;
}

async function getPaymentRequests(query) {
  const queryString = query ? `?${query}` : "";
  try {
    const response = await api.get(`api/paymentrequests${queryString}`);
    return response.data;
  } catch (error) {
    console.error(error);
    return [];
  }
}

// React Query path: throws on error, supports cancellation via signal.
// Prefer this over getPaymentRequests for new call sites.
async function fetchPaymentRequests(query, { signal } = {}) {
  const queryString = query ? `?${query}` : "";
  const response = await api.get(`api/paymentrequests${queryString}`, {
    signal,
  });
  return response.data;
}

// Task-manager dashboard metrics over a period ("Today" | "ThisWeek" | "ThisMonth"): collected +
// requests sent (payment-service), documents sent (activities), AI answers (intelligence). Throws on
// error so the caller can show its own fallback rather than misleading zeros.
async function fetchTaskMetrics(period = "Today", { signal } = {}) {
  const response = await api.get("api/metrics", {
    params: { period },
    signal,
  });
  return response.data;
}

// Both throw on error so callers can roll back optimistic updates.
async function setPaymentRequestLabel(id, type) {
  await api.put(`api/paymentrequests/${id}/labels/${type}`);
}

async function removePaymentRequestLabel(id, type) {
  await api.delete(`api/paymentrequests/${id}/labels/${type}`);
}

export {
  createPaymentRequest,
  cancelPayment,
  markPaymentRequestPaid,
  createpaymentRequestReminderAsync,
  getPaymentDetails,
  fetchPaymentDetails,
  getPaymentRequests,
  fetchPaymentRequests,
  fetchTaskMetrics,
  setPaymentRequestLabel,
  removePaymentRequestLabel,
};
